import { useEffect, useMemo, useRef } from "react";

import { keepPreviousData } from "@tanstack/react-query";

import { useGetQuotes } from "~/v2/api/hooks/useGetQuotes";
import { useMarketDataFeed } from "~/v2/api/hooks/useMarketDataFeed";
import type { Quote } from "~/v2/api/rest/data";
import type { EnrichedAsset } from "./AssetSearch.types";
import { getAssetQuotePricing } from "./assetSearch.utils";
import type { AssetSearchResult } from "./useAssetSearchQueries";

export interface UseAssetSearchQuotesInput {
  assets: readonly AssetSearchResult[];
}

export interface AssetSearchQuotesValue {
  assets: EnrichedAsset[];
}

export const useAssetSearchQuotes = ({
  assets,
}: UseAssetSearchQuotesInput): AssetSearchQuotesValue => {
  const symbols = useMemo(() => assets.map((a) => a.symbol), [assets]);

  const { latestFeed } = useMarketDataFeed();

  const {
    quotes,
    isLoading: isQuoteLoading,
    isFetching: isQuoteFetching,
  } = useGetQuotes({
    symbols,
    feed: latestFeed,
    options: {
      enabled: symbols.length > 0,
      placeholderData: keepPreviousData,
    },
  });

  const previousQuotesRef = useRef<Quote[]>([]);

  const allQuotes = useMemo(() => {
    const currentQuotes = quotes ?? [];
    const previousQuotes = previousQuotesRef.current;

    const quoteMap = new Map<string, Quote>();
    for (const q of previousQuotes) {
      quoteMap.set(q.symbol, q);
    }
    for (const q of currentQuotes) {
      quoteMap.set(q.symbol, q);
    }

    return Array.from(quoteMap.values());
  }, [quotes]);

  useEffect(() => {
    previousQuotesRef.current = allQuotes;
  }, [allQuotes]);

  const enrichedAssets: EnrichedAsset[] = useMemo(() => {
    const quoteMap = new Map(allQuotes.map((q) => [q.symbol, q]));
    return assets.map((asset) => {
      const quote = quoteMap.get(asset.symbol);
      const assetQuoteLoading = (isQuoteLoading || isQuoteFetching) && !quote;

      const isCrypto = asset.class === "crypto";
      const showNoOvernightSupportIcon =
        !isCrypto &&
        asset.tradable &&
        !asset.attributes?.includes("overnight_tradable");
      const showOvernightHaltedIcon =
        !isCrypto &&
        asset.tradable &&
        !showNoOvernightSupportIcon &&
        (asset.attributes?.includes("overnight_halted") ?? false);

      if (quote) {
        const { price, dailyChange, dailyChangePercent } =
          getAssetQuotePricing(quote);

        return {
          id: asset.id,
          symbol: asset.symbol,
          name: asset.name,
          status: asset.status,
          tradable: asset.tradable,
          showOvernightHaltedIcon,
          showNoOvernightSupportIcon,
          price,
          dailyChange,
          dailyChangePercent,
          isQuoteLoading: false,
        };
      }

      return {
        id: asset.id,
        symbol: asset.symbol,
        name: asset.name,
        status: asset.status,
        tradable: asset.tradable,
        showOvernightHaltedIcon,
        showNoOvernightSupportIcon,
        isQuoteLoading: assetQuoteLoading,
      };
    });
  }, [assets, allQuotes, isQuoteLoading, isQuoteFetching]);

  return { assets: enrichedAssets };
};
